import { logger } from '@/lib/logger';
/**
 * 🔒 AUDIT FIX: Safe localStorage wrapper com tratamento de erros
 * 
 * Previne crashes em:
 * - Safari/Firefox Private Mode
 * - Quota exceeded (5MB limit)
 * - localStorage desabilitado
 * 
 * Issue #3 da Auditoria Completa
 * ✅ AUDIT FIX: Migrado console.* para logger.*
 * 
 * NOTE: Este módulo NÃO importa logger para evitar dependência circular.
 * Usa console.warn/error diretamente apenas neste arquivo.
 * O logger.ts importa indiretamente de módulos que podem usar storage.
 */

interface StorageItem<T> {
  data: T;
  timestamp: number;
  version?: string;
}

/**
 * Internal safe log — storage.ts cannot import logger (circular dep risk).
 * Uses console directly, but only in dev.
 */
const storageLog = {
  warn: (...args: unknown[]) => {
    if (import.meta.env.DEV) logger.warn(...args);
  },
  error: (...args: unknown[]) => {
    logger.error(...args); // errors always logged
  },
  info: (...args: unknown[]) => {
    if (import.meta.env.DEV) logger.info(...args);
  },
};

/**
 * Wrapper seguro para localStorage com fallback e gestão de erros
 */
export const storage = {
  /**
   * Obtém item do localStorage com fallback
   */
  get<T>(key: string, fallback: T): T {
    try {
      const item = localStorage.getItem(key);
      if (!item) return fallback;

      const parsed = JSON.parse(item);
      return parsed;
    } catch (error) {
      if (error instanceof DOMException) {
        storageLog.warn(`[storage] localStorage bloqueado ou indisponível para key: ${key}`);
      } else {
        storageLog.warn(`[storage] Erro ao ler ${key}:`, error);
      }
      return fallback;
    }
  },

  /**
   * Armazena item no localStorage com tratamento de quota
   */
  set<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'QuotaExceededError') {
          storageLog.error('[storage] Quota excedida. Limpando dados antigos...');
          
          if (typeof window !== 'undefined' && window.document) {
            import('@/hooks/use-toast').then(({ toast }) => {
              toast({
                title: "Armazenamento quase cheio",
                description: "Dados antigos foram removidos automaticamente para liberar espaço.",
                variant: "default",
              });
            }).catch(() => {
              storageLog.warn('[storage] Toast not available for user notification');
            });
          }
          
          storage.clearOldData();
          
          try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
          } catch (retryError) {
            storageLog.error('[storage] Falha ao salvar mesmo após limpeza:', retryError);
            
            if (typeof window !== 'undefined' && window.document) {
              import('@/hooks/use-toast').then(({ toast }) => {
                toast({
                  title: "Erro ao salvar preferências",
                  description: "Não foi possível salvar suas preferências locais. Seu navegador pode estar em modo anônimo ou o armazenamento está cheio.",
                  variant: "destructive",
                });
              }).catch(() => {});
            }
            
            return false;
          }
        } else {
          storageLog.warn(`[storage] localStorage bloqueado ou indisponível para key: ${key}`);
          
          if (typeof window !== 'undefined' && window.document) {
            import('@/hooks/use-toast').then(({ toast }) => {
              toast({
                title: "Armazenamento indisponível",
                description: "Suas preferências não serão salvas. Você pode estar navegando em modo anônimo.",
                variant: "default",
              });
            }).catch(() => {});
          }
        }
      } else {
        storageLog.warn(`[storage] Erro ao salvar ${key}:`, error);
      }
      return false;
    }
  },

  /**
   * Remove item do localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      storageLog.warn(`[storage] Erro ao remover ${key}:`, error);
    }
  },

  /**
   * Verifica se key existe
   */
  has(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },

  /**
   * Limpa dados antigos (autosave, drafts com mais de 7 dias)
   */
  clearOldData(): void {
    try {
      const now = Date.now();
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      const keys = Object.keys(localStorage);

      keys.forEach(key => {
        if (key.startsWith('autosave-') || key.startsWith('draft-')) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const parsed = JSON.parse(item);
              const timestamp = parsed.timestamp;
              
              if (timestamp && (now - new Date(timestamp).getTime()) > sevenDaysMs) {
                localStorage.removeItem(key);
                storageLog.info(`[storage] Removido item antigo: ${key}`);
              }
            }
          } catch {
            localStorage.removeItem(key);
          }
        }
      });

      storageLog.info('[storage] Limpeza de dados antigos concluída');
    } catch (error) {
      storageLog.warn('[storage] Erro ao limpar dados antigos:', error);
    }
  },

  /**
   * Obtém tamanho aproximado do localStorage em bytes
   */
  getSize(): number {
    try {
      let total = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += key.length + (localStorage.getItem(key)?.length || 0);
        }
      }
      return total;
    } catch {
      return 0;
    }
  },

  /**
   * Verifica se localStorage está disponível
   */
  isAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Armazena com timestamp automático
   */
  setWithTimestamp<T>(key: string, data: T): boolean {
    const item: StorageItem<T> = {
      data,
      timestamp: Date.now(),
      version: import.meta.env.VITE_BUILD_VERSION || '1.0.0',
    };
    return storage.set(key, item);
  },

  /**
   * Obtém com validação de timestamp (expira após TTL)
   */
  getWithExpiry<T>(key: string, fallback: T, ttlMs: number): T {
    try {
      const item = storage.get<StorageItem<T> | null>(key, null);
      
      if (!item) return fallback;
      
      const now = Date.now();
      if (now - item.timestamp > ttlMs) {
        storage.remove(key);
        return fallback;
      }
      
      return item.data;
    } catch {
      return fallback;
    }
  }
};
